import type React from "react"
import { useEffect } from "react"
import styled from "styled-components"
import backgroundImage from "~/assets/arbeidsark-bg-illustration.svg"
import { Button, OutlineButton } from "~/components"
import Toast from "~/modules/toasts/Toast"
import { useTranslate } from "~/ur-react-hooks"
import { useToast } from "~/utils"
import { useErrorHandling } from "~/utils/error"
import useCountdown from "~/utils/hooks/useCountdown"
import { useLogout } from "~/utils/hooks/useLogout"
import { trpc } from "~/utils/trpc"

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;

  background-image: url(${backgroundImage});

  ${(props) =>
    props.theme.media.mobile &&
    `
      width: 100%;
  `}
`

const Title = styled.h3`
  font-family: 'Work Sans';
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 145%;
  margin-top: 1rem;
  margin-bottom: 1rem;
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 456px;
  box-sizing: border-box;

  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0px 8px 32px 0px rgba(27, 12, 55, 0.12);
`

const Description = styled.p`
  line-height: 150%;
  margin-bottom: 2rem;
  margin-top: 1rem;
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export type SingleUserEmailConfirmationProps = {}

const SingleUserEmailConfirmation: React.FC<
  SingleUserEmailConfirmationProps
> = () => {
  const translations = useTranslate({
    title: "single-user-email-confirmation.title",
    toast: "single-user-email-confirmation.toast",
    description: "single-user-email-confirmation.description",
    buttonWithCount: [
      "single-user-email-confirmation.button-with-count",
      { count: 0 },
    ],
    buttonWithoutCount: "single-user-email-confirmation.button-without-count",
    newEmailSent: "single-user-email-confirmation.new-email-sent",
    logout: "common.log-out",
  })

  const [count, { resetCountdown, startCountdown }] = useCountdown({
    countStart: 60,
    intervalMs: 1000,
  })

  useEffect(() => {
    startCountdown()
  }, [startCountdown])

  const toast = useToast()
  const logout = useLogout()

  const { trpcErrorHandler } = useErrorHandling()

  const { mutate: sendNewConfirmationEmail, isPending: isLoading } =
    trpc.authentication.sendNewConfirmationEmailToUser.useMutation({
      onSuccess() {
        toast("success", translations.newEmailSent)
      },
      onError: trpcErrorHandler(toast),
      onSettled() {
        resetCountdown()
        startCountdown()
      },
    })

  return (
    <Wrapper>
      <Card>
        <Title>{translations.title}</Title>
        <Toast type="success" message={translations.toast} />
        <Description>{translations.description}</Description>
        <Actions>
          <Button
            disabled={count > 0 || isLoading}
            onClick={() => {
              sendNewConfirmationEmail()
              startCountdown()
            }}
          >
            {count > 0
              ? translations.buttonWithCount({ count })
              : translations.buttonWithoutCount}
          </Button>
          <OutlineButton onClick={logout} width="100%">
            {translations.logout}
          </OutlineButton>
        </Actions>
      </Card>
    </Wrapper>
  )
}

export default SingleUserEmailConfirmation
